import { useState } from 'react';
import { X, Gavel, Package, CalendarDays, CalendarClock } from 'lucide-react';
import { Button } from '../../ui/button';
import { purchasingAPI, GoodsReceiptNote } from '../../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { useFormatters } from '../../../contexts/PreferencesContext';
import FileDropZone from '../../common/FileDropZone';

interface RaiseAppealModalProps {
  isOpen: boolean;
  onClose: () => void;
  grn: GoodsReceiptNote;
  onSuccess: () => void;
  existingAppealId?: number;
}

// CR-4740: Removed unit_price as an appealable field
type AppealType = 'quantity' | 'delivery_date' | 'odd';

const APPEAL_OPTIONS: { key: AppealType; label: string; icon: typeof Package }[] = [
  { key: 'delivery_date', label: 'Delivery Date', icon: CalendarDays },
  { key: 'quantity', label: 'Quantity', icon: Package },
  { key: 'odd', label: 'Original Due Date', icon: CalendarClock },
];

const REASON_CODES = [
  'Delivered on time - see attached POD',
  'Incorrect receipt date recorded',
  'Early delivery agreed with customer',
  'Delivery date changed by customer',
  'Quantity discrepancy',
  'System error',
  'Other',
];

export default function RaiseAppealModal({ isOpen, onClose, grn, onSuccess, existingAppealId }: RaiseAppealModalProps) {
  const isUpdate = !!existingAppealId;
  const [selectedTypes, setSelectedTypes] = useState<Set<AppealType>>(new Set());
  const [qtyClaimed, setQtyClaimed] = useState(String(grn.quantity_received || ''));
  const [dateClaimed, setDateClaimed] = useState(grn.receipt_date || '');
  // Customer review 11 Sep (item 36): the ODD shown and claimed against
  // is the date the On-Time verdict was measured on (backend verdict engine),
  // not the line's re-planned delivery date — an accepted ODD claim rewrites
  // both. Older payloads fall back to the line delivery date.
  const currentOdd = grn.verdict_anchor_date ?? grn.po_delivery_date ?? '';
  const [oddClaimed, setOddClaimed] = useState(currentOdd);
  const [supplierNote, setSupplierNote] = useState('');
  const [qtyReasonCode, setQtyReasonCode] = useState('');
  const [dateReasonCode, setDateReasonCode] = useState('');
  const [oddReasonCode, setOddReasonCode] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { formatDate } = useFormatters();

  if (!isOpen) return null;

  const toggleType = (type: AppealType) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedTypes.size === 0) {
      toast.error('Please select at least one item to appeal');
      return;
    }

    const hasQtyChange = selectedTypes.has('quantity') && qtyClaimed !== String(grn.quantity_received);
    const hasDateChange = selectedTypes.has('delivery_date') && dateClaimed !== grn.receipt_date;
    const hasOddChange = selectedTypes.has('odd') && oddClaimed !== currentOdd;

    if (!hasQtyChange && !hasDateChange && !hasOddChange) {
      toast.error('Please change at least one value from the current');
      return;
    }

    // Validate that each changed field has a reason code
    if (hasQtyChange && !qtyReasonCode) {
      toast.error('Please select a reason code for the quantity change');
      return;
    }
    if (hasDateChange && !dateReasonCode) {
      toast.error('Please select a reason code for the delivery date change');
      return;
    }
    if (hasOddChange && !oddReasonCode) {
      toast.error('Please select a reason code for the ODD change');
      return;
    }

    try {
      setSubmitting(true);
      // Build note from per-field reason codes + additional comments
      const reasonParts: string[] = [];
      if (hasQtyChange && qtyReasonCode) reasonParts.push(`Quantity: ${qtyReasonCode}`);
      if (hasDateChange && dateReasonCode) reasonParts.push(`Delivery Date: ${dateReasonCode}`);
      if (hasOddChange && oddReasonCode) reasonParts.push(`ODD: ${oddReasonCode}`);
      const reasonSummary = reasonParts.join('; ');
      const note = supplierNote ? `${reasonSummary} - ${supplierNote}` : reasonSummary;
      const data: Record<string, string | number> = {
        grn: grn.id,
        supplier_note: note,
      };
      if (hasQtyChange) data['qty_claimed'] = qtyClaimed;
      if (hasDateChange) data['date_claimed'] = dateClaimed;
      if (hasOddChange) data['odd_claimed'] = oddClaimed;

      let appealId: number;
      if (isUpdate && existingAppealId) {
        await purchasingAPI.updateDeliveryAppeal(existingAppealId, data as Parameters<typeof purchasingAPI.updateDeliveryAppeal>[1]);
        appealId = existingAppealId;
      } else {
        const res = await purchasingAPI.createDeliveryAppeal(data as Parameters<typeof purchasingAPI.createDeliveryAppeal>[0]);
        appealId = res.data.id;
      }

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await purchasingAPI.uploadAppealAttachment(appealId, formData);
      }

      toast.success(isUpdate ? 'Appeal updated successfully' : 'Appeal submitted successfully');
      // CR-4869 round 2: await onSuccess so the parent list/modal re-fetch
      // completes BEFORE we close the modal — Jacqueline's bounce said the
      // list kept showing "not appealed" until a manual refresh. Closing
      // first let the list render stale data and the refetch that followed
      // wasn't visible without a scroll/refresh nudge.
      await Promise.resolve(onSuccess());
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to submit appeal'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {/* Header. CR-7319: the gavel of the control that opened it. */}
          <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center gap-2">
              <Gavel className="h-5 w-5 text-orange-500" />
              <h3 className="text-lg font-bold">{isUpdate ? 'Update Appeal' : 'Raise Appeal'}</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* GRN Context */}
          <div className="px-5 pt-4">
            <div className="bg-gray-50 rounded-lg p-3 text-sm grid grid-cols-2 gap-2">
              <div><span className="text-gray-500">GRN:</span> <span className="font-medium">{grn.grn_number}</span></div>
              <div><span className="text-gray-500">PO:</span> <span className="font-medium">{grn.po_number}</span></div>
              <div><span className="text-gray-500">Part:</span> <span className="font-medium">{grn.part_number}</span></div>
              <div><span className="text-gray-500">On-Time:</span> <span className="font-medium capitalize">{grn.on_time_status || '-'}</span></div>
            </div>
            {/* CR-4653: Key delivery dates for context */}
            <div className="bg-blue-50 rounded-lg p-3 text-sm grid grid-cols-3 gap-2 mt-2">
              <div><span className="text-gray-500">Delivery Date:</span><br /><span className="font-medium">{grn.receipt_date ? formatDate(grn.receipt_date) : '-'}</span></div>
              <div><span className="text-gray-500">MRP Date:</span><br /><span className="font-medium">{grn.mrp_date ? formatDate(grn.mrp_date) : '-'}</span></div>
              <div><span className="text-gray-500">ODD:</span><br /><span className="font-medium">{currentOdd ? formatDate(currentOdd) : '-'}</span></div>
            </div>
          </div>

          {/* What would you like to appeal? */}
          <div className="px-5 pt-4">
            <p className="text-sm font-semibold text-orange-700 mb-3">What would you like to appeal?</p>
            <div className="flex flex-wrap gap-2">
              {APPEAL_OPTIONS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => toggleType(key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    selectedTypes.has(key)
                      ? 'border-orange-500 bg-orange-50 text-orange-700 font-medium'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Dynamic Input Sections */}
          <div className="p-5 space-y-4">
            {selectedTypes.has('quantity') && (
              <div className="border rounded-lg p-3 bg-orange-50/30 space-y-2">
                <label className="text-sm font-medium block mb-1">
                  Quantity Claimed
                  <span className="text-gray-400 ml-2 font-normal">Current: {grn.quantity_received}</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={qtyClaimed}
                  onChange={(e) => setQtyClaimed(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {qtyClaimed !== String(grn.quantity_received) && (
                  <div>
                    <label className="text-xs font-medium text-orange-700 block mb-1">Reason Code *</label>
                    <select
                      value={qtyReasonCode}
                      onChange={(e) => setQtyReasonCode(e.target.value)}
                      className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Select a reason...</option>
                      {REASON_CODES.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {selectedTypes.has('delivery_date') && (
              <div className="border rounded-lg p-3 bg-orange-50/30 space-y-2">
                <label className="text-sm font-medium block mb-1">
                  Delivery Date Claimed
                  <span className="text-gray-400 ml-2 font-normal">Current: {formatDate(grn.receipt_date)}</span>
                </label>
                <input
                  type="date"
                  value={dateClaimed}
                  onChange={(e) => setDateClaimed(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {dateClaimed !== grn.receipt_date && (
                  <div>
                    <label className="text-xs font-medium text-orange-700 block mb-1">Reason Code *</label>
                    <select
                      value={dateReasonCode}
                      onChange={(e) => setDateReasonCode(e.target.value)}
                      className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Select a reason...</option>
                      {REASON_CODES.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {selectedTypes.has('odd') && (
              <div className="border rounded-lg p-3 bg-orange-50/30 space-y-2">
                <label className="text-sm font-medium block mb-1">
                  Original Delivery Date (ODD) Claimed
                  <span className="text-gray-400 ml-2 font-normal">Current: {currentOdd ? formatDate(currentOdd) : '-'}</span>
                </label>
                <input
                  type="date"
                  value={oddClaimed}
                  onChange={(e) => setOddClaimed(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {oddClaimed !== currentOdd && (
                  <div>
                    <label className="text-xs font-medium text-orange-700 block mb-1">Reason Code *</label>
                    <select
                      value={oddReasonCode}
                      onChange={(e) => setOddReasonCode(e.target.value)}
                      className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Select a reason...</option>
                      {REASON_CODES.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {selectedTypes.size === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Select one or more options above to begin your appeal
              </p>
            )}

            {/* Additional Comments */}
            <div>
              <label className="text-sm font-medium block mb-1">Additional Comments</label>
              <textarea
                value={supplierNote}
                onChange={(e) => setSupplierNote(e.target.value)}
                placeholder="Provide additional details about the proposed change..."
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
              />
            </div>

            {/* Attachments */}
            <div>
              <label className="text-sm font-medium block mb-1">Attachments</label>
              {/* Customer review 11 Sep (drag and drop everywhere). */}
              <FileDropZone
                multiple
                size="compact"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif"
                hint="PDF, Word, Excel, CSV or images"
                onFiles={setFiles}
                data-testid="raise-appeal-drop-zone"
              />
              {files.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{files.length} file(s) selected</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-5 border-t sticky bottom-0 bg-white">
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || selectedTypes.size === 0}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {submitting ? 'Submitting...' : isUpdate ? 'Update Appeal' : `Submit Appeal`}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
